import { useEffect, useState } from "react";
import { Loader2, MessageSquare, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

// A line or two from friends reading a shared folder.
export const FolderComments = ({ token, canPost = false, canDelete = false }) => {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState(() => localStorage.getItem("chatmilu.news.commentname") || "");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.get(`/news/shared/${token}/comments`)
      .then(({ data }) => setRows(data.comments))
      .catch(() => {});
  }, [token]);

  const post = async () => {
    const said = text.trim();
    if (!said || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/news/shared/${token}/comments`, { name, text: said });
      setRows((r) => [...r, data]);
      setText("");
      localStorage.setItem("chatmilu.news.commentname", name);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not leave that note");
    } finally {
      setBusy(false);
    }
  };

  const drop = async (comment_id) => {
    try {
      await api.delete(`/news/shared/${token}/comments/${comment_id}`);
      setRows((r) => r.filter((c) => c.comment_id !== comment_id));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not remove that note");
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-panel p-4" data-testid="folder-comments">
      <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-ink-soft">
        <MessageSquare className="h-3.5 w-3.5 text-brand" /> Talking about these
        {rows.length > 0 && <span className="text-brand">{rows.length}</span>}
      </p>

      {rows.length === 0 && (
        <p className="mt-2 text-xs text-ink-soft" data-testid="folder-comments-empty">
          {canPost ? "Be the first to say something." : "No notes yet."}
        </p>
      )}

      <ul className="mt-2 space-y-2">
        {rows.map((c) => (
          <li key={c.comment_id} className="flex items-start gap-2 text-sm" dir="auto"
              data-testid={`folder-comment-${c.comment_id}`}>
            <span className="shrink-0 font-semibold text-brand">{c.name}</span>
            <span className="min-w-0 flex-1">{c.text}</span>
            {canDelete && (
              <button
                data-testid={`folder-comment-drop-${c.comment_id}`}
                aria-label="Remove this note"
                onClick={() => drop(c.comment_id)}
                className="text-ink-soft/60 transition-colors hover:text-ink"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </li>
        ))}
      </ul>

      {canPost && (
        <div className="mt-3 flex flex-wrap gap-2">
          <input
            data-testid="folder-comment-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={40}
            placeholder="Your name"
            className="min-h-[36px] w-32 rounded-full border border-white/15 bg-page px-3 text-xs outline-none focus:border-brand/50"
          />
          <input
            data-testid="folder-comment-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && post()}
            maxLength={400}
            placeholder="Say something about these stories…"
            className="min-h-[36px] min-w-0 flex-1 rounded-full border border-white/15 bg-page px-4 text-xs outline-none focus:border-brand/50"
          />
          <button
            data-testid="folder-comment-send"
            onClick={post}
            disabled={busy || !text.trim()}
            className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full bg-brand px-4 text-xs font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Leave it
          </button>
        </div>
      )}
    </div>
  );
};
